import { type FC, type ReactNode, useEffect, useRef, useState } from "react";
import GroupUsersList from "../GroupUsersList/GroupUsersList";
import { connect, type ConnectedProps } from "react-redux";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { bindAction } from "../../../../../../interfaces";
import { fetchGroupUsersForList } from "../../state/thunks/groupMembersInfoThunk";
import { resetSelectedItems, selectedItemsChanged } from "../../state/slices/groupMembersInfoSlice";
import { usersFilter } from "../../../../../../components/filterForms/UsersFilterForm/usersFilter";
import userListUtils from "../../../../../../utils/userListUtils";
import { type SortingDirection } from "../../../../../../enums";
import type { Filters } from "../../../../../../utils/queryUtils";
import { bindActionCreators } from "redux";
import {
  resetAppliedFilter,
  resetSearch,
  setAppliedFilter,
  setSearch,
} from "../../state/slices/groupMembersFiltersSlice";
import { fetchGroupUsersOptions } from "../../state/thunks/groupMembersFiltersThunk";
import { isEmpty } from "lodash";
import UsersAssignmentModalContainer from "./Containers/UsersAssignmentModalContainer/UsersAssignmentModalContainer";
import RemoveMembersConfirmationModalContainer from "./Containers/RemoveMembersConfirmationModalContainer/RemoveMembersConfirmationModalContainer";
import UsersFileUploadModalContainer from "./Containers/UsersFileUploadModalContainer/UsersFileUploadModalContainer";
import { MembersModalEnum, setActiveModal, setShowLicenceStep } from "../../state/slices/groupMembersModalsSlice";
import { shouldShowLicenseModalForGroupUpload } from "../../../../../Licensing/ContentAssignmentModalLicensingSteps/state/thunks/licensingModalThunk";

export interface Props extends PropsFromRedux {
  groupId: number;
  addMembersBtn: ReactNode;
}
export const ManageMembers: FC<Props> = (props) => {
  const { resetSelectedIdsAction, resetSearchAction, resetAppliedFilterAction } = props;
  const reloadListItems = useRef<(enableSorting: boolean) => void>();
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  const loadMembersPage = (
    skip: number,
    top: number,
    sortingColumnName: string,
    sortingDirection: SortingDirection,
    appliedFilter: Filters,
  ) => {
    const groupId = props.groupId;
    const filterParams = props.search ? appliedFilter : usersFilter.buildFilterQuery(appliedFilter);
    const orderByParams = userListUtils.formatOrderParamsV2(sortingColumnName, sortingDirection);

    props.fetchUsersAction({ groupId, skip, top, orderByParams, filter: filterParams, search: props.search });
  };

  const createReloadListItems = (reloadListItemsFunc: (enableSorting: boolean) => void) => {
    reloadListItems.current = reloadListItemsFunc;
  };

  const onSearchMembersChanged = (search: string) => {
    props.setSearchAction(search);
    reloadListItems.current?.(isEmpty(search));
  };

  const onRemoveMemberClick = (userId: number) => {
    props.selectedItemsAction([userId]);
    props.setActiveModalAction(MembersModalEnum.RemoveUsersModal);
  };

  useEffect(() => {
    return () => {
      resetSelectedIdsAction();
      resetSearchAction();
      resetAppliedFilterAction();
    };
  }, [resetSelectedIdsAction, resetSearchAction, resetAppliedFilterAction]);

  const onFileDropped = (file: File) => {
    setUploadedFile(file);
    props.shouldShowLicenseStepForGroupUploadAction([props.groupId]).then((shouldShowStep: boolean) => {
      props.setActiveModalAction(MembersModalEnum.FileUploadModal);
      props.setShowLicenceStepAction(shouldShowStep);
    });
  };
  return (
    <>
      <GroupUsersList
        isDragAndDrop
        isGroupCreation={false}
        isAutoEnrollment={false}
        users={props.items}
        usersCount={props.itemsCount}
        isLoading={props.isLoading}
        appliedUserFilter={props.appliedFilter}
        setUserFilter={props.applyFilterAction}
        resetUserFilter={props.resetAppliedFilterAction}
        selectedItemIds={props.selectedIds}
        updateSelectedItems={props.selectedItemsAction}
        loadPage={loadMembersPage}
        renderHeaderSection
        onRemoveMemberClick={onRemoveMemberClick}
        filterOptions={props.filterOptions}
        getFilterOptions={props.fetchGroupUsersOptionsAction}
        setReloadListItems={createReloadListItems}
        search={props.search}
        onSearchChanged={onSearchMembersChanged}
        deepLink
        addMembersBtn={props.addMembersBtn}
        onFileDropped={onFileDropped}
      />

      {props.activeModal === MembersModalEnum.AddUsersModal && (
        <UsersAssignmentModalContainer groupId={props.groupId} />
      )}
      {props.activeModal === MembersModalEnum.RemoveUsersModal && (
        <RemoveMembersConfirmationModalContainer groupId={props.groupId} />
      )}
      <UsersFileUploadModalContainer
        groupId={props.groupId}
        showModal={props.activeModal === MembersModalEnum.FileUploadModal}
        uploadedFile={uploadedFile}
        setUploadedFile={setUploadedFile}
      />
    </>
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => ({
  selectedIds: state.people.addGroup.groupMembers.overview.list.selectedIds,
  activeModal: state.people.addGroup.groupMembers.modalOptions.activeModal,
  items: state.people.addGroup.groupMembers.overview.list.items,
  search: state.people.addGroup.groupMembers.overview.filter.search,
  appliedFilter: state.people.addGroup.groupMembers.overview.filter.appliedFilter,
  filterOptions: state.people.addGroup.groupMembers.overview.filter.filterOptions,
  itemsCount: state.people.addGroup.groupMembers.overview.list.itemsCount,
  isLoading: state.people.addGroup.groupMembers.overview.list.isLoading,
});

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  fetchUsersAction: bindActionCreators(fetchGroupUsersForList, dispatch),
  fetchGroupUsersOptionsAction: bindActionCreators(fetchGroupUsersOptions, dispatch),
  selectedItemsAction: bindAction(selectedItemsChanged, dispatch),
  setSearchAction: bindAction(setSearch, dispatch),
  resetSearchAction: bindAction(resetSearch, dispatch),
  applyFilterAction: bindAction(setAppliedFilter, dispatch),
  resetAppliedFilterAction: bindAction(resetAppliedFilter, dispatch),
  resetSelectedIdsAction: bindAction(resetSelectedItems, dispatch),
  setActiveModalAction: bindAction(setActiveModal, dispatch),
  shouldShowLicenseStepForGroupUploadAction: bindAction(shouldShowLicenseModalForGroupUpload, dispatch),
  setShowLicenceStepAction: bindAction(setShowLicenceStep, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(ManageMembers);
export default ConnectedComponent;
